<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { Banner, Button, FormControl, Modal, Option, Select } from '@ui'
import type { SelectOption } from '@ui'
import { useUsers } from '@/api/users'
import type { AppUser } from '@/types/api'

/**
 * Pick who to view the product as (US-21). Only active users and managers are
 * offered — admins are refused by the API, and there is nothing to learn from
 * seeing your own view again.
 */
const props = defineProps<{ open: boolean; pending?: boolean; selfId: string }>()
const emit = defineEmits<{ close: []; start: [userId: string] }>()

const { t } = useI18n()
const users = useUsers()

const candidates = computed<AppUser[]>(() =>
  (users.data.value ?? []).filter(
    (user) => user.status === 'active' && user.role !== 'admin' && user.id !== props.selfId,
  ),
)

const options = computed<SelectOption[]>(() =>
  candidates.value.map((user) => ({
    label: user.name,
    value: user.id,
    description: `${t(`users.roles.${user.role}`)} · ${user.email}`,
  })),
)

const selected = ref<string | null>(null)
const selectedOption = computed(() => options.value.find((o) => o.value === selected.value))
const pick = (option: SelectOption | SelectOption[] | undefined): void => {
  const value = Array.isArray(option) ? option[0]?.value : option?.value
  selected.value = value ? String(value) : null
}

watch(
  () => props.open,
  (open) => {
    if (open) selected.value = null
  },
)
</script>

<template>
  <Modal
    :model-value="open"
    :title="t('viewAs.title')"
    size="sm"
    type="content"
    no-icon
    @close="emit('close')"
  >
    <template #content>
      <div class="view-as">
        <p class="view-as__hint">{{ t('viewAs.description') }}</p>
        <Banner
          v-if="!users.isLoading.value && candidates.length === 0"
          variant="neutral"
          :title="t('viewAs.noCandidates')"
        />
        <FormControl v-else :label="t('viewAs.pick')" required>
          <Select
            :model-value="selectedOption"
            :options="options"
            data-test="view-as-user"
            @update:model-value="pick"
          >
            <Option
              v-for="option in options"
              :key="option.value"
              :label="option.label"
              :value="option.value"
              :description="option.description"
            />
          </Select>
        </FormControl>
      </div>
    </template>
    <template #actions>
      <Button type="secondary" @click="emit('close')">{{ t('common.cancel') }}</Button>
      <Button
        :loading="pending"
        :disabled="!selected"
        :aria-disabled="!selected"
        data-test="view-as-start"
        @click="selected && emit('start', selected)"
      >
        {{ t('viewAs.start') }}
      </Button>
    </template>
  </Modal>
</template>

<style scoped>
.view-as {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-lg);
}

.view-as__hint {
  margin: 0;
  font-size: var(--font-size-body-3);
  line-height: var(--line-height-body-3);
  color: var(--fg-muted);
}
</style>
